import * as React from 'react';
import { useEffect } from 'react';
import {
    BoltIcon,
    ShieldCheckIcon,
    SparklesIcon,
} from '@heroicons/react/24/outline';
import { Calendar } from '../../../common/conversion/calendar';
import { scrollToTop } from '../../../common/utils';

const highlights = [
    {
        title: 'Architecture Review',
        description:
            'Walk through your current stack with our engineers and map the fastest path to APIs, agents, and MCP servers.',
        icon: SparklesIcon,
    },
    {
        title: 'Tailored Roadmap',
        description:
            'See how Pellerex automates delivery, governance, and observability for the workloads that matter most to you.',
        icon: BoltIcon,
    },
    {
        title: 'Security & Compliance',
        description:
            'Discuss SOC 2, ISO 27001, data residency, and the controls our enterprise customers rely on every day.',
        icon: ShieldCheckIcon,
    },
];

const trustPoints = [
    'Zero commitment',
    '30 minute working session',
    'Solution oriented — no sales pitch',
];

const TalkToExperts = () => {
    useEffect(() => {
        scrollToTop();
    }, []);

    return (
        <main className="relative isolate overflow-hidden bg-gray-900">
            <style>{`
                @keyframes tte-amber-shine {
                    0%   { background-position: 0% 50%; }
                    100% { background-position: 220% 50%; }
                }
                @keyframes tte-live-ping {
                    0%   { transform: scale(1);   opacity: 0.7; }
                    80%  { transform: scale(2.6); opacity: 0; }
                    100% { transform: scale(2.6); opacity: 0; }
                }
                .tte-amber-word {
                    background: linear-gradient(
                        100deg,
                        #d9f99d 0%,
                        #a3e635 18%,
                        #84cc16 38%,
                        #ecfccb 58%,
                        #84cc16 78%,
                        #a3e635 100%
                    );
                    background-size: 220% auto;
                    -webkit-background-clip: text;
                    background-clip: text;
                    color: transparent;
                    animation: tte-amber-shine 7s ease-in-out infinite;
                    font-weight: 700;
                    letter-spacing: -0.01em;
                    filter:
                        drop-shadow(0 0 8px rgba(163,230,53,0.18))
                        drop-shadow(0 0 16px rgba(132,204,22,0.12));
                }
                .tte-pill {
                    position: relative;
                    overflow: hidden;
                    box-shadow:
                        0 0 14px rgba(99,102,241,0.20),
                        0 0 28px rgba(129,140,248,0.10);
                }
                .tte-live-dot {
                    position: relative;
                    display: inline-block;
                    width: 0.5rem;
                    height: 0.5rem;
                    border-radius: 9999px;
                    background: #a3e635;
                    box-shadow: 0 0 8px rgba(163,230,53,0.7);
                    flex: none;
                }
                .tte-live-dot::before,
                .tte-live-dot::after {
                    content: "";
                    position: absolute;
                    inset: 0;
                    border-radius: 9999px;
                    background: rgba(163,230,53,0.55);
                    animation: tte-live-ping 2.2s cubic-bezier(0.33,0,0.2,1) infinite;
                }
                .tte-live-dot::after { animation-delay: 1.1s; }
                .tte-card {
                    position: relative;
                    border-radius: 1rem;
                    background: linear-gradient(180deg, rgba(15,23,42,0.7) 0%, rgba(15,23,42,0.55) 100%);
                    border: 1px solid rgba(255,255,255,0.10);
                    transition: transform 280ms cubic-bezier(0.33,0,0.2,1), border-color 280ms ease, box-shadow 280ms ease;
                }
                .tte-card:hover {
                    transform: translateY(-3px);
                    border-color: rgba(163,230,53,0.35);
                    box-shadow:
                        0 18px 40px -20px rgba(2,6,23,0.8),
                        0 0 0 1px rgba(163,230,53,0.15),
                        0 0 24px rgba(163,230,53,0.10);
                }
                .tte-card-icon {
                    background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(56,189,248,0.18) 100%);
                    border: 1px solid rgba(255,255,255,0.10);
                    color: #d9f99d;
                }
                /* Override the global "body p { color: $darkTextColor }" + default p margins */
                .tte-card-title {
                    color: #ffffff !important;
                    font-size: 1.0625rem;
                    line-height: 1.4;
                    letter-spacing: -0.005em;
                    margin: 0 !important;
                }
                .tte-card-desc {
                    color: #cbd5e1 !important;
                    font-size: 0.9rem;
                    line-height: 1.55;
                    margin: 0.375rem 0 0 0 !important;
                }
                .tte-lede {
                    color: #cbd5e1 !important;
                    margin: 1.5rem 0 0 0 !important;
                    letter-spacing: -0.005em;
                }
                .tte-trust-item { color: #e5e7eb !important; }
                .tte-trust-check { color: #a3e635; }
                .tte-page-bg {
                    position: absolute;
                    inset: 0;
                    pointer-events: none;
                    background:
                        radial-gradient(60% 50% at 15% 0%, rgba(99,102,241,0.18) 0%, transparent 70%),
                        radial-gradient(50% 45% at 85% 10%, rgba(56,189,248,0.14) 0%, transparent 70%),
                        radial-gradient(60% 50% at 50% 100%, rgba(129,140,248,0.10) 0%, transparent 70%);
                }

            `}</style>

            <div aria-hidden="true" className="tte-page-bg" />

            <div className="relative mx-auto max-w-7xl px-6 pt-16 pb-10 sm:pt-24 sm:pb-12 lg:px-8 lg:pt-32 lg:pb-16">
                <div className="grid grid-cols-1 gap-x-12 gap-y-12 lg:grid-cols-12">
                    <div className="lg:col-span-5 xl:col-span-5">
                        <span className="tte-pill inline-flex items-center gap-x-2 rounded-full bg-indigo-500/10 px-3 py-1 text-sm font-semibold leading-7 text-indigo-300 ring-1 ring-inset ring-indigo-500/20">
                            <span aria-hidden="true" className="tte-live-dot" />
                            Schedule a demo
                        </span>

                        <h1 className="mt-8 text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-5xl">
                            Talk to an{' '}
                            <span className="tte-amber-word">Expert</span>
                        </h1>

                        <p className="tte-lede mt-6 text-lg leading-8">
                            Pick a time that works for you. Spend 30 minutes with
                            a senior Pellerex architect to map a fast, governed
                            path to APIs, agents, and MCP servers — built for
                            your stack.
                        </p>

                        <ul className="mt-10 space-y-6">
                            {highlights.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <li
                                        key={item.title}
                                        className="tte-card flex items-start gap-x-4 px-5 py-4"
                                    >
                                        <span className="tte-card-icon flex h-10 w-10 flex-none items-center justify-center rounded-lg">
                                            <Icon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="tte-card-title text-base font-semibold">
                                                {item.title}
                                            </h3>
                                            <p className="tte-card-desc mt-1 text-sm leading-6">
                                                {item.description}
                                            </p>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>

                        <ul className="mt-10 space-y-3">
                            {trustPoints.map((point) => (
                                <li
                                    key={point}
                                    className="tte-trust-item flex items-center gap-x-3 text-sm font-medium"
                                >
                                    <svg
                                        aria-hidden="true"
                                        viewBox="0 0 20 20"
                                        className="tte-trust-check h-5 w-5 flex-none"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.6"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M5 10.5l3 3 7-7.5" />
                                    </svg>
                                    {point}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-7 xl:col-span-7">
                        <Calendar eventId={'pellerex/consultation'} />
                    </div>
                </div>
            </div>
        </main>
    );
};

export default TalkToExperts;
